/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { DateTime, Duration, DurationUnit, LocaleOptions } from "luxon";

type PossibleDate = Date | string | null;

/*
*   '~~' ist ein doppeltes bitweises negieren und wird verwendet, um Zahlen Richtung 0 zu runden.
*   Bitweise Operationen haben allerdings auch das "Feature", dass die Zahl zuvor auf 32 Bit gekürzt wird.
*   '~~' deshalb nur verwenden, wenn der Wert nicht größer als ein 32-Bit Integer wird.
*   Jahre, Monate, Tage, Stunden, Minuten sind sicher. 2^31 Sekunden sind ca. 68 Jahre, zur Sicherheit besser roundToZero() verwenden.
*   2^31 Millisekunden sind ca. 25 Tage, hier muss auf jeden Fall roundToZero() verwendet werden.
*/

export default class DateUtil {
    public static toDateTimeInputValue(date: Date): string {
        const yyyy = date.getFullYear().toString().padStart(4, "0");
        const MM = (date.getMonth() + 1).toString().padStart(2, "0");
        const dd = date.getDate().toString().padStart(2, "0");
        const hh = date.getHours().toString().padStart(2, "0");
        const mm = date.getMinutes().toString().padStart(2, "0");
        return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
    }

    public static toDateInputValue(date: Date): string {
        const yyyy = date.getFullYear().toString().padStart(4, "0");
        const MM = (date.getMonth() + 1).toString().padStart(2, "0");
        const dd = date.getDate().toString().padStart(2, "0");
        return `${yyyy}-${MM}-${dd}`;
    }

    public static get today(): Date {
        const now = DateUtil.now;
        return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    }

    public static addDays(value: string, days: number): string {
        const dateTime = DateUtil.parse(value);
        const durationObject = Duration.fromObject({ days: ~~days });
        return dateTime.plus(durationObject).toISO() ?? "";
    }

    public static addMilliseconds(value: string, duration: number): string {
        const dateTime = DateUtil.parse(value)
        const durationObject = Duration.fromMillis(this.roundToZero(duration));
        return dateTime.plus(durationObject).toISO() ?? "";
    }

    public static isValidDate(value?: PossibleDate): boolean {
        if (!value) {
            return false;
        }

        return !isNaN(Date.parse(value.toString()));
    }

    public static setTime(value: string, timeValue: string, includeOffset?: boolean): string {
        let dateTime = DateUtil.parse(value);
        const timeSpan = DateUtil.parse(timeValue);
        dateTime = dateTime.set({
            hour: ~~timeSpan.hour,
            minute: ~~timeSpan.minute,
            second: this.roundToZero(timeSpan.second),
            millisecond: this.roundToZero(timeSpan.millisecond),
        });
        return dateTime.toISO({ includeOffset: includeOffset }) ?? "";
    }

    public static toDatumString(value: string) {
        const datetime = DateTime.fromISO(value, { zone: "utc", setZone: true });
        return datetime.toISODate() ?? "";
    }

    public static getDiffInMillis(value1: string, value2: string): number {
        return this.roundToZero(DateUtil.diff(value1, value2).toMillis());
    }

    public static getDayDiffInMillis(value1: string, value2: string): number {
        return ~~(DateUtil.diff(value1, value2, ["days", "hours", "minutes", "seconds", "milliseconds"]).days) * 24 * 60 * 60 * 1000;
    }

    public static isOnSameDay(value1: string, value2: string): boolean {
        const dateTime1 = DateUtil.parse(value1);
        const dateTime2 = DateUtil.parse(value2);
        return dateTime1.hasSame(dateTime2, "day");
    }

    public static parse(value: string, localeOptions?: LocaleOptions): DateTime {
        return DateTime.fromISO(value, { zone: "utc", setZone: true, ...localeOptions });
    }

    public static removeTime(date: Date | string) {
        const d = date instanceof Date ? date : new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    }

    public static diffInMillis(a: Date | string, b: Date | string): number {
        a = a instanceof Date ? a : new Date(a);
        b = b instanceof Date ? b : new Date(b);
        return new Date(a).getTime() - new Date(b).getTime();
    }

    public static min(a: Date | string, b: Date | string): Date {
        a = a instanceof Date ? a : new Date(a);
        b = b instanceof Date ? b : new Date(b);
        return a.getTime() <= b.getTime() ? a : b;
    }

    public static max(a: Date | string, b: Date | string): Date {
        a = new Date(a);
        b = new Date(b);
        return a.getTime() >= b.getTime() ? a : b;
    }

    public static getNowDateTimeInputValue = () => this.toDateTimeInputValue(this.now);

    public static isValidDateObject(value: Date): boolean {
        return value instanceof Date && !isNaN(value.getTime());
    }

    public static findIndexOfClosestFutureDateRelativeToReference(values: (Date | string)[], reference: Date | string): number {
        let idx = -1;
        let closestDelta = Number.POSITIVE_INFINITY;
        const referenceTime = DateUtil.getTime(reference);
        for (let i = 0; i < values.length; i++) {
            const delta = DateUtil.getTime(values[i]) - referenceTime
            if (delta >= 0) {
                if (delta < closestDelta) {
                    closestDelta = delta;
                    idx = i;
                }
            }
        }

        return idx;
    }

    public static getTime(value: Date | string) {
        let time = typeof value === "string" ? Date.parse(value) : value.getTime();
        if (isNaN(time)) {
            time = Number.NEGATIVE_INFINITY;
        }

        return time
    }

    private static diff(value1: string, value2: string, unit?: DurationUnit | DurationUnit[]): Duration {
        const dateTime1 = DateUtil.parse(value1);
        const dateTime2 = DateUtil.parse(value2);
        return dateTime2.diff(dateTime1, unit);
    }

    private static get now(): Date {
        return new Date();
    }

    // Spiegelt das Verhalten von '~~' wieder, ohne auf 32-Bit zu kürzen
    private static roundToZero(input: number): number {
        if (isNaN(input)) {
            return 0;
        }

        return input >= 0 ? Math.floor(input) : Math.ceil(input);
    }
}
